import { z } from 'zod';
import { User } from './user.js';
import { PortalPreviewContext } from './portal-preview.js';

export const LoginRequest = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/**
 * Login response. `token` is only populated for bearer-token clients
 * (mobile apps). Web clients receive the session via an httpOnly cookie
 * and should ignore this field.
 */
export const LoginResponse = z.object({
  user: User,
  token: z.string().optional(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

/**
 * Set when the signed-in session was opened via admin impersonation.
 * Lets the web client render an "Impersonating X — Exit" banner to
 * the admin without changing how the rest of the UI behaves. The
 * impersonated user themselves never sees this — they only ever
 * receive their own /auth/me response with `impersonating: null`.
 */
export const ImpersonationContext = z.object({
  admin: z.object({
    id: z.number().int().positive(),
    name: z.string(),
  }),
});
export type ImpersonationContext = z.infer<typeof ImpersonationContext>;

export const MeResponse = z.object({
  user: User,
  impersonating: ImpersonationContext.nullable(),
  /**
   * Set only on the website while an admin previews a client's portal
   * read-only ("Preview as client"). `user` is then the client as the portal
   * sees them. Optional: web-public prerenders against the live API.
   */
  portalPreview: PortalPreviewContext.nullable().optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const LogoutResponse = z.object({
  ok: z.literal(true),
});
export type LogoutResponse = z.infer<typeof LogoutResponse>;

export const ExitImpersonationResponse = z.object({
  user: User,
  /** The admin's original bearer-token session, returned so mobile
   *  clients can swap back. Absent for cookie-authenticated web clients
   *  (the cookie swap happens server-side). */
  token: z.string().optional(),
});
export type ExitImpersonationResponse = z.infer<typeof ExitImpersonationResponse>;

/**
 * The caller's MCP connection details — the public MCP endpoint URL and their
 * personal connection token (a dedicated `client:'mcp'` session). `token` is
 * null until one is generated. Used by Settings → "Connect an AI assistant".
 */
export const McpConnectionResponse = z.object({
  mcpUrl: z.string(),
  token: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type McpConnectionResponse = z.infer<typeof McpConnectionResponse>;
